import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { isValid, parseISO } from 'date-fns';
import { useEffect } from 'react';
import type {
  IAuditImpactAreaItem,
  IAuditUrgencyItem,
} from '@/api/report/types';
import { useAuditTypes } from '@/app/(client)/audits/-hooks/useAuditsQuery';
import { useFacilityProcessFilterOptions } from '@/components/shared/FacilityProcessOptions/hooks/useFacilityProcessFilterOptions';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  useCollaborationStatusOptions,
  useNetworkCompanyOptions,
} from '@/hooks/useDropdowns';
import { useTranslator } from '@/hooks/useTranslator';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { useSessionStore } from '@/store/sessionStore';
import { AuditFindingsUrgencyChart } from '../../../-components/audit-compliance/AuditFindingsUrgencyChart';
import { AuditKpiGrid } from '../../../-components/audit-compliance/AuditKpiGrid';
import { AuditsByImpactAreasTable } from '../../../-components/audit-compliance/AuditsByImpactAreasTable';
import { AuditsByStandardChart } from '../../../-components/audit-compliance/AuditsByStandardChart';
import { AuditsByTypeChart } from '../../../-components/audit-compliance/AuditsByTypeChart';
import { HistoricDataRangePicker } from '../../../-components/HistoricDataRangePicker';
import { ReportToolbar } from '../../../-components/ReportToolbar';
import { ReportWidgetBoundary } from '../../../-components/ReportWidgetBoundary';
import {
  useAuditMetricsQuery,
  useAuditUrgenciesQuery,
} from '../../../-hooks/useAuditComplianceQuery';
import { useAuditFilters } from '../../../-hooks/useAuditFilters';
import { useReportFilterPreferences } from '../../../-hooks/useReportFilterPreferences';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(analytics)/audit-compliance/',
)({
  component: AuditCompliancePage,
});

function AuditCompliancePage() {
  const { getTranslation: t } = useTranslator({ page: 'auditCompliance' });
  const navigate = useNavigate();
  const _companyId = useSessionStore(s => s.user?.companyId ?? '');

  const suppliers = useNetworkCompanyOptions();
  const collabStatuses = useCollaborationStatusOptions();
  const auditTypesResult = useAuditTypes();
  const auditTypeOptions = (auditTypesResult.data?.data ?? []).map(at => ({
    key: at.id,
    label: at.name,
  }));

  const {
    filters,
    setFilters,
    appliedFilter,
    filterDefs,
    dateRange,
    setDateRange,
  } = useAuditFilters({
    supplierOptions: suppliers.data ?? [],
    collaborationStatusOptions: collabStatuses.data ?? [],
    processOptions: useFacilityProcessFilterOptions(),
    countryOptions: COUNTRY_OPTIONS,
    auditTypeOptions,
  });

  const { saveFilter } = useReportFilterPreferences('rpt-audit-compliance');
  useEffect(() => {
    saveFilter(appliedFilter);
  }, [appliedFilter, saveFilter]);

  const { data: metricsData, isLoading } = useAuditMetricsQuery(appliedFilter);
  const { data: urgenciesData } = useAuditUrgenciesQuery(appliedFilter);

  const DRILLDOWN_ALLOWED_KEYS = new Set([
    'supplierCompanyIds',
    'collaborationStatusIds',
    'facilityProcesses',
    'countries',
    'auditTypeIds',
    'auditTypeImpactAreaId',
    'auditFindingUrgency',
    'startDate',
    'endDate',
    'upcomingAudits',
  ]);

  const openDrilldown = (
    chartType: string,
    extra?: Record<string, unknown>,
  ) => {
    const base = (() => {
      try {
        return JSON.parse(appliedFilter) as Record<string, unknown>;
      } catch {
        return {};
      }
    })();
    const merged = { ...base, ...extra };
    const drilldownFilter = Object.fromEntries(
      Object.entries(merged).filter(([k]) => DRILLDOWN_ALLOWED_KEYS.has(k)),
    );
    void navigate({
      to: '/report/audit-compliance/drilldown/$chartType',
      params: { chartType },
      search: { filter: JSON.stringify(drilldownFilter) },
    });
  };

  const handleUrgencyBarClick = (
    urgencyId: string,
    isPrevious: boolean,
    item: IAuditUrgencyItem,
  ) => {
    const extra: Record<string, unknown> = { auditFindingUrgency: urgencyId };
    if (isPrevious) {
      const { startDate, endDate } = item.previousCountDateRange;
      if (
        startDate &&
        endDate &&
        isValid(parseISO(startDate)) &&
        isValid(parseISO(endDate))
      ) {
        extra.startDate = startDate;
        extra.endDate = endDate;
      }
    }
    openDrilldown('auditFindingsByUrgency', extra);
  };

  const handleImpactAreaDrilldown = (row: IAuditImpactAreaItem) => {
    openDrilldown('auditByImpactAreas', {
      hierarchyId: row.hierarchyId,
      impactArea: row.impactArea,
      auditTypeImpactAreaId: row.impactAreaId,
    });
  };

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-3 z-10'>
        <ReportToolbar
          filterDefs={filterDefs}
          filters={filters}
          onFiltersChange={setFilters}
          helpUrl='https://app.intercom.com/a/apps/11060514-introduction-to-the-audit-compliance-report'
        />
      </div>

      <div className='flex-1 min-h-0 overflow-y-auto'>
        <div className='flex flex-col gap-6 p-4 pb-10'>
          {/* Each widget is isolated: these charts and tables come from separate
              endpoints, and one bad response shape used to blank the whole page
              via the page-level boundary. `resetKey` retries on a filter change. */}
          <ReportWidgetBoundary resetKey={appliedFilter}>
            <AuditKpiGrid
              data={metricsData}
              loading={isLoading}
              onDrilldown={openDrilldown}
              auditTypeFilterActive={!!filters.auditTypeIds?.length}
            />
          </ReportWidgetBoundary>

          {/* Historic data section */}
          <div className='flex items-start justify-between gap-4'>
            <div className='flex flex-col gap-1'>
              <p className='text-sm font-semibold text-foreground leading-tight'>
                {t('historicData.title')}
              </p>
              <p className='text-sm text-muted-foreground'>
                {t('historicData.description')}
              </p>
            </div>
            <HistoricDataRangePicker
              selected={dateRange}
              onChange={setDateRange}
              placeholder={t('historicData.selectRange')}
            />
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <ReportWidgetBoundary resetKey={appliedFilter}>
              <AuditsByTypeChart
                data={metricsData?.auditsByType}
                loading={isLoading}
              />
            </ReportWidgetBoundary>
            <ReportWidgetBoundary resetKey={appliedFilter}>
              <AuditsByStandardChart
                data={metricsData?.auditsByStandard}
                loading={isLoading}
              />
            </ReportWidgetBoundary>
          </div>

          <ReportWidgetBoundary resetKey={appliedFilter}>
            <AuditFindingsUrgencyChart
              data={urgenciesData?.data}
              loading={isLoading}
              onBarClick={handleUrgencyBarClick}
            />
          </ReportWidgetBoundary>

          <ReportWidgetBoundary resetKey={appliedFilter}>
            <AuditsByImpactAreasTable
              filter={appliedFilter}
              onDrilldown={handleImpactAreaDrilldown}
            />
          </ReportWidgetBoundary>
        </div>
      </div>
    </PageContainer>
  );
}
